import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Drawer,
  List,
  Toolbar,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import SidebarSection from './SidebarSection';
import {
  drawerWidth,
  miniDrawerWidth,
  getSidebarConfig,
} from './sidebarConfig';

const DARK = '#1A3A5C';

export default function AppLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, userData } = useAuth();
  const { accessibleBranches, activeBranchId, setActiveBranchId, isAdmin } = useBranch();

  const [drawerOpen, setDrawerOpen] = useState(true);
  const [openByLabel, setOpenByLabel] = useState({});
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  const modules = useMemo(() => {
    return getSidebarConfig(userData?.role, { isAdmin });
  }, [userData?.role, isAdmin]);

  const isModuleActive = (m) =>
    location.pathname === m.path ||
    (m.items || []).some((item) => location.pathname === item.path);

  const currentWidth = drawerOpen ? drawerWidth : miniDrawerWidth;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${currentWidth}px)`,
          ml: `${currentWidth}px`,
          bgcolor: '#E3F2FD',
          color: DARK,
          transition: (theme) =>
            theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src="/logo-udla.png"
              alt="UDLA"
              sx={{ height: 40, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
            <Box
              component="img"
              src="/HOSPITAL-VETERINARIO.png"
              alt="Hospital Veterinario"
              sx={{ height: 40 }}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ textAlign: 'right', mr: 2, lineHeight: 1.2 }}>
            <Typography variant="body2" fontWeight={600} noWrap color="inherit">
              {userData?.name || userData?.email || 'Usuario'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, color: 'inherit' }}>
              {userData?.role || ''}
            </Typography>
          </Box>
          {accessibleBranches.length > 0 && (
            <FormControl size="small" sx={{ mr: 2, minWidth: 180 }}>
              <Select
                value={activeBranchId}
                onChange={(e) => setActiveBranchId(e.target.value)}
                startAdornment={
                  <StorefrontIcon sx={{ fontSize: 18, mr: 1, color: 'inherit' }} />
                }
                sx={{
                  color: 'inherit',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(26,58,92,0.5)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(26,58,92,0.8)' },
                  '.MuiSvgIcon-root': { color: 'inherit' },
                  '.MuiSelect-select': { py: 0.75 },
                }}
              >
                {isAdmin && <MenuItem value="all">Todas las sucursales</MenuItem>}
                {accessibleBranches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Mi perfil">
            <IconButton
              onClick={(e) => setProfileAnchorEl(e.currentTarget)}
              sx={{ p: 0.5 }}
            >
              <Avatar
                sx={{
                  width: 36, height: 36, bgcolor: DARK, fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
              >
                {(userData?.name || userData?.email || '?')[0]?.toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={profileAnchorEl}
            open={Boolean(profileAnchorEl)}
            onClose={() => setProfileAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 220, mt: 1 } }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {userData?.name || 'Usuario'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {userData?.email}
              </Typography>
              <Typography variant="caption" color="primary" display="block">
                {userData?.role || ''}
              </Typography>
            </Box>
            <MenuItem onClick={() => { setProfileAnchorEl(null); navigate('/profile'); }}>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Mi Perfil</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Cerrar Sesión</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: currentWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          '& .MuiDrawer-paper': {
            width: currentWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: drawerOpen ? 'space-between' : 'center',
            px: 1.5,
          }}
        >
          {drawerOpen && (
            <Box
              component="img"
              src="/logo-horizontal.png"
              alt="Spherevet"
              sx={{ height: 44, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
          )}
          <Tooltip title={drawerOpen ? 'Contraer menú' : 'Expandir menú'}>
            <IconButton
              onClick={() => setDrawerOpen((o) => !o)}
              size="small"
              sx={{ color: DARK }}
            >
              {drawerOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
            </IconButton>
          </Tooltip>
        </Toolbar>

        <List sx={{ pt: 0.5 }}>
          {modules.map((m) => {
            const active = isModuleActive(m);
            const isOpen =
              openByLabel[m.label] !== undefined ? openByLabel[m.label] : active;
            return (
              <SidebarSection
                key={m.label}
                id={m.label}
                label={m.label}
                icon={m.icon}
                path={m.path}
                mini={!drawerOpen}
                open={isOpen}
                active={active}
                onClick={() => {
                  const current =
                    openByLabel[m.label] !== undefined
                      ? openByLabel[m.label]
                      : active;
                  setOpenByLabel((prev) => ({
                    ...prev,
                    [m.label]: !current,
                  }));
                  navigate(m.path);
                }}
                items={m.items || []}
              />
            );
          })}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3, overflowX: 'hidden', maxWidth: '100%' }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
